import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { EditorHero, MonoLabel, Monogram, Pill } from './Editorial';
import { RailFrame, usesRail } from './AdminShell';
import './ProfileShell.css';

/**
 * AthleteProfileShell + CoachProfileShell
 *
 * Wraps an editor page with the editorial hero (slate gradient, monogram tile,
 * name, meta, stat strip) + tab strip. Page content goes in children below.
 *
 * Both shells fetch the user's own profile so the hero stays current — pages
 * that also fetch /athletes/profile or /coaches/profile for their own form
 * data simply duplicate one read; harmless.
 */

const ATHLETE_TABS = [
  { label: 'Info',         to: '/profile' },
  { label: 'Academics',    to: '/academics' },
  { label: 'Photos',       to: '/photos' },
  { label: 'Videos',       to: '/videos' },
  { label: 'Social',       to: '/social' },
  { label: 'My Sports',    to: '/my-sports' },
  { label: 'Stats',        to: '/stats' },
  { label: 'Measurables',  to: '/measurables' },
  { label: 'Preferences',  to: '/preferences' },
];

const COACH_TABS = [
  { label: 'Info',      to: '/coach-profile' },
  { label: 'Academics', to: '/coach-academics' },
  { label: 'Photos',    to: '/coach-photos' },
  { label: 'Videos',    to: '/coach-videos' },
  { label: 'Social',    to: '/coach-social' },
];

// Admin profile currently mirrors the coach "Info" page only (no sub-sections
// yet). Reuses the coach profile endpoint (admins are allowed on /coaches/*).
const ADMIN_TABS = [
  { label: 'Info', to: '/admin-profile' },
];

const formatHeight = (inches) => {
  if (!inches) return null;
  // Round to whole inches BEFORE dividing — flooring first then rounding the
  // remainder renders 71.6 as 5'12" instead of 6'0".
  const total = Math.round(inches);
  return `${Math.floor(total / 12)}'${total % 12}"`;
};

// Staff and partners see their own profile inside the rail: a plain identity row and, when the
// profile has sections, the .adm-tabs strip. Coaches keep the editorial hero (see the shells below).
function RailProfile({ activeTab, tabs, name, initials, meta, publicUrl, children }) {
  return (
    <RailFrame active={null}>
      <header className="prf-id">
        <span className="prf-id__avatar" aria-hidden="true">{initials}</span>
        <div className="prf-id__text">
          <h1 className="prf-id__name">{name || 'Profile'}</h1>
          {meta && <p className="prf-id__meta">{meta}</p>}
        </div>
        {publicUrl && (
          <a className="adm-btn prf-id__public" href={publicUrl} target="_blank" rel="noopener noreferrer">View public profile ↗</a>
        )}
      </header>
      {tabs.length > 1 && (
        <nav className="adm-tabs" aria-label="Profile sections">
          {tabs.map((tab) => (tab.label === activeTab
            ? <span key={tab.label} className="adm-tab adm-tab--active" aria-current="page">{tab.label}</span>
            : <Link key={tab.label} to={tab.to} className="adm-tab">{tab.label}</Link>))}
        </nav>
      )}
      {children}
    </RailFrame>
  );
}

function TabStrip({ tabs, activeLabel }) {
  return (
    <div className="ed-row" style={{ flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
      {tabs.map(tab =>
        tab.label === activeLabel ? (
          <Pill key={tab.label} variant="active">{tab.label}</Pill>
        ) : (
          <Link key={tab.label} to={tab.to} style={{ textDecoration: 'none' }}>
            <Pill variant="ghost">{tab.label}</Pill>
          </Link>
        )
      )}
    </div>
  );
}

/**
 * AthleteProfileShell
 *
 * Props:
 *   activeTab — label of current tab (must match one in ATHLETE_TABS)
 *   eyebrowLabel — override the eyebrow's first mono label (defaults to "PROFILE · {ACTIVETAB}")
 *   children — page body
 */
export function AthleteProfileShell({ activeTab, eyebrowLabel, children }) {
  const { user, API_URL } = useAuth();
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    let cancel = false;
    axios.get(`${API_URL}/athletes/profile`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    })
      .then(r => { if (!cancel) setProfile(r.data || {}); })
      .catch(() => { if (!cancel) setProfile({}); });
    return () => { cancel = true; };
  }, [API_URL]);

  const p = profile || {};
  const initials = ((user?.first_name || '')[0] || '') + ((user?.last_name || '')[0] || '');
  const sport = (p.sports_played || []).join(', ');
  const positions = [p.position_primary, p.position_secondary].filter(Boolean).join(' / ');
  const sportLine = [sport, positions].filter(Boolean).join(' · ');
  const subParts = [];
  if (p.graduation_year) subParts.push(`Class of '${p.graduation_year.toString().slice(-2)}`);
  const schoolName = p.school ?? p.high_school;
  if (schoolName) subParts.push([schoolName, [p.city, p.state].filter(Boolean).join(', ')].filter(Boolean).join(' — '));
  if (sportLine) subParts.push(sportLine);

  const stats = [
    { label: 'HEIGHT', value: formatHeight(p.height_inches) },
    { label: 'WEIGHT', value: p.weight_lbs ? `${p.weight_lbs}` : null },
    { label: '40-YARD', value: p.dash_40 || null },
    { label: 'VERTICAL', value: p.vertical_jump ? `${p.vertical_jump}"` : null },
    { label: 'GPA', value: p.gpa_weighted || p.gpa_unweighted || null },
  ];

  const eyebrow = eyebrowLabel || `PROFILE · ${activeTab.toUpperCase()}`;

  return (
    <div className="ed-page">
      <EditorHero
        eyebrow={
          <>
            <MonoLabel onDark>{eyebrow}</MonoLabel>
            {schoolName && <MonoLabel onDark>·</MonoLabel>}
            {schoolName && <MonoLabel onDark>{schoolName.toUpperCase()}</MonoLabel>}
          </>
        }
        prefix={<Monogram initials={initials} photoUrl={p.player_photo || null} />}
        headline={`${user?.first_name || ''} ${user?.last_name || ''}`.trim()}
        sub={subParts.join(' · ')}
        stats={stats}
      />

      <TabStrip tabs={ATHLETE_TABS} activeLabel={activeTab} />

      {children}
    </div>
  );
}

/**
 * CoachProfileShell
 *
 * Props:
 *   activeTab — label of current tab (must match one in COACH_TABS)
 *   children — page body
 */
export function CoachProfileShell({ activeTab, children }) {
  const { user, API_URL } = useAuth();
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    let cancel = false;
    axios.get(`${API_URL}/coaches/profile`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    })
      .then(r => { if (!cancel) setProfile(r.data || {}); })
      .catch(() => { if (!cancel) setProfile({}); });
    return () => { cancel = true; };
  }, [API_URL]);

  const p = profile || {};
  const initials = ((user?.first_name || '')[0] || '') + ((user?.last_name || '')[0] || '');
  const subParts = [];
  if (p.school_name) subParts.push(p.school_name);
  if (p.position_title) subParts.push(p.position_title);
  if (p.sport) subParts.push(p.sport);
  if (p.division) subParts.push(p.division);

  // Role-aware eyebrow label — partner uses the same editor as coach,
  // basic_coach is a coach awaiting credential review.
  const roleLabel =
    user?.role === 'partner'      ? 'PARTNER' :
    user?.role === 'basic_coach'  ? 'COACH · PENDING' :
                                    'COACH';

  if (usesRail(user?.role)) {
    const titleCase = roleLabel.charAt(0) + roleLabel.slice(1).toLowerCase();
    return (
      <RailProfile activeTab={activeTab} tabs={COACH_TABS} initials={initials.toUpperCase()}
        name={`${user?.first_name || ''} ${user?.last_name || ''}`.trim()}
        meta={[titleCase, p.position_title, p.school_name].filter(Boolean).join(' · ')}
        publicUrl={user?.role === 'partner' && p.uid && p.profile_url ? `/coach/${p.uid}/${p.profile_url}` : null}>
        {children}
      </RailProfile>
    );
  }

  return (
    <div className="ed-page">
      <EditorHero
        eyebrow={
          <>
            <MonoLabel onDark>{roleLabel} · {activeTab.toUpperCase()}</MonoLabel>
            {p.school_name && <MonoLabel onDark>·</MonoLabel>}
            {p.school_name && <MonoLabel onDark>{p.school_name.toUpperCase()}</MonoLabel>}
          </>
        }
        prefix={<Monogram initials={initials} photoUrl={p.player_photo || null} />}
        headline={`${user?.first_name || ''} ${user?.last_name || ''}`.trim()}
        sub={subParts.join(' · ')}
      />

      <TabStrip tabs={COACH_TABS} activeLabel={activeTab} />

      {children}
    </div>
  );
}

/**
 * AdminProfileShell
 *
 * Admin's own profile — currently mirrors the coach shell (reads /coaches/profile
 * for the hero + form). Placeholder home for admin-specific fields to come.
 *
 * Props:
 *   activeTab — label of current tab (must match one in ADMIN_TABS)
 *   children — page body
 */
export function AdminProfileShell({ activeTab, children }) {
  const { user, API_URL } = useAuth();
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    let cancel = false;
    axios.get(`${API_URL}/coaches/profile`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    })
      .then(r => { if (!cancel) setProfile(r.data || {}); })
      .catch(() => { if (!cancel) setProfile({}); });
    return () => { cancel = true; };
  }, [API_URL]);

  const p = profile || {};
  const initials = ((user?.first_name || '')[0] || '') + ((user?.last_name || '')[0] || '');

  if (usesRail(user?.role)) {
    return (
      <RailProfile activeTab={activeTab} tabs={ADMIN_TABS} initials={initials.toUpperCase()}
        name={`${user?.first_name || ''} ${user?.last_name || ''}`.trim()}
        meta={[user?.role === 'internal_staff' ? 'Staff' : 'Admin', user?.email].filter(Boolean).join(' · ')}>
        {children}
      </RailProfile>
    );
  }

  return (
    <div className="ed-page">
      <EditorHero
        eyebrow={<MonoLabel onDark>ADMIN · {activeTab.toUpperCase()}</MonoLabel>}
        prefix={<Monogram initials={initials} photoUrl={p.player_photo || null} />}
        headline={`${user?.first_name || ''} ${user?.last_name || ''}`.trim()}
        sub={user?.email || ''}
      />

      <TabStrip tabs={ADMIN_TABS} activeLabel={activeTab} />

      {children}
    </div>
  );
}
